import { Typography } from 'components';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { LOW_STOCK_THRESHOLD } from 'constants/parts';

import theme from 'themes';
import { Product } from 'types/parts';
import { getStockText } from 'common/helpers/v1';
import lightTheme from 'common/theme/light.theme';
import Ban from 'icons/indexed-icons/Ban';
import Exclamation from 'icons/indexed-icons/Exclamation';
import BadgeCheck from 'icons/indexed-icons/BadgeCheck';
import { getLeadTimeTextComponent } from '.';
import { ContentAlign } from 'common/components/common-styled';
import {
  FavoriteText,
  SavedProduct
} from 'components/MachinePartsTable/PartsView/PartViewFlyout.elements';
import Heart from 'icons/indexed-icons/Heart';

const StockIndicatorContainer = styled.div`
  display: flex;
  justify-content: space-between;
  .icon {
    width: 1.2rem;
  }
`;
const LeadTime = styled.div`
  width: auto;
  margin-left: auto;
`;
const StockText = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;
const IconAndTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;
const getIcon = (stock?: number) => {
  if (stock === undefined || stock <= 0) {
    return <Ban color={theme.colors.error200} />;
  } else if (stock < LOW_STOCK_THRESHOLD && stock > 0) {
    return <Exclamation color={lightTheme.status.warning.base} />;
  } else {
    return <BadgeCheck color={theme.colors.green2} />;
  }
};

export interface StockIndicatorProps {
  purchasable: Product | undefined;
  isSavedProduct?: boolean;
  handleSave?: () => void;
  handleDelete?: () => void;
}

function getIndicatorColor(stock: number | undefined): string {
  if (stock === undefined) {
    return theme.colors.error200;
  } else if (stock < LOW_STOCK_THRESHOLD && stock > 0) {
    return lightTheme.status.warning.base;
  } else if (stock <= 0) {
    return theme.colors.error200;
  }
  return theme.colors.green2;
}

const StockIndicator2 = ({
  purchasable,
  isSavedProduct,
  handleDelete,
  handleSave
}: StockIndicatorProps): ReactElement => {
  const stock = purchasable?.stock;
  const { t } = useTranslation(['fpns']);
  return (
    <StockIndicatorContainer className="stock-view">
      <ContentAlign gap=".5rem">
        <IconAndTextWrapper>
          <SavedProduct
            onClick={() => {
              isSavedProduct ? handleDelete?.() : handleSave?.();
            }}
          >
            <Heart isFilled={isSavedProduct} />{' '}
            <FavoriteText>Save{isSavedProduct && <>d</>} as Favorite</FavoriteText>
          </SavedProduct>
          <StockText>
            {getIcon(stock)}
            <Typography mb={0} color={getIndicatorColor(stock)} weight="semi-bold">
              {t(getStockText(purchasable))}
            </Typography>
          </StockText>
        </IconAndTextWrapper>

        <LeadTime className="lead-time">
          {purchasable?.leadTime !== undefined && getLeadTimeTextComponent(purchasable, t)}
        </LeadTime>
      </ContentAlign>
    </StockIndicatorContainer>
  );
};

export default StockIndicator2;
